// components/footfall/PlanSendToSlides.tsx
'use client';

import { useState } from 'react';
import { Presentation } from 'lucide-react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { PILL_CLASS, PILL_STYLE } from '@/components/slides/SlidesReviewLauncher';
import { sendScenarioToSlides } from './planToSlides';

/**
 * Above a Plan conversation's composer, as Slides keeps its review launcher there: the scenario on the map, once the
 * user is happy with it, goes to Slides as a presentation. The button says what it is doing while it works.
 */
export function PlanSendToSlides() {
  const { kernel } = useKernel();
  const fr = useKernelStore(s => s.language) === 'fr';
  const isPlanSession = useKernelStore(s => {
    const id = s.activeChatSessionId;
    return !!id && s.inventory.ChatSession.registry[id]?.creationModule === 'pulse';
  });
  const scenarioName = useKernelStore(s => {
    const d = s.pulse.draft;
    if (!d || (!d.moves.length && !d.layout?.length)) return null;
    return d.scenarios?.find(v => v.id === d.activeScenarioId)?.name ?? null;
  });
  const [step, setStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!isPlanSession || !scenarioName) return null;
  const send = async () => {
    const k = kernel.current;
    if (!k || step) return;
    setError(null);
    setStep(fr ? 'Préparation' : 'Preparing');
    try {
      const refusal = await sendScenarioToSlides(k, setStep);
      if (refusal) setError(refusal);
    } catch {
      setError(fr ? 'La présentation n’a pas pu être créée.' : 'The presentation could not be created.');
    } finally {
      setStep(null);
    }
  };
  return <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2" data-testid="plan-send-to-slides">
    <button type="button" className={`${PILL_CLASS} inline-flex items-center gap-1.5`} style={PILL_STYLE} disabled={!!step} onClick={() => void send()}
      title={fr ? `Créer dans Slides la présentation du scénario « ${scenarioName} » : le plan, sa justification et la short-list` : `Create the presentation of "${scenarioName}" in Slides: the plan, its rationale and the shortlist`}>
      <Presentation size={14} className="shrink-0 text-[#B4B4B4]" />
      {step ? `${step}…` : fr ? 'Envoyer vers Slides' : 'Send to Slides'}
    </button>
    {error && <span role="alert" className="text-[#F28B82]" style={{ fontSize: 12 }}>{error}</span>}
  </div>;
}
